/**
 * KAN-952: the "С чего начать" button is hidden for good once its cross is
 * clicked (per browser, localStorage). The state is shared between the button
 * and the layout (which reserves room for it) through a tiny store.
 * Storage errors are ignored: without storage the button is shown and a
 * dismissal lasts until reload.
 */
import { useCallback, useSyncExternalStore } from 'react';

export const ONBOARDING_DISMISSED_KEY = 'onboardingButtonDismissed';

/** Dismissal made in this page load (also covers unavailable storage). */
let dismissedInMemory = false;
const listeners = new Set<() => void>();

function readDismissed(): boolean {
  if (dismissedInMemory) return true;
  try {
    return localStorage.getItem(ONBOARDING_DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function dismissOnboarding(): void {
  dismissedInMemory = true;
  try {
    localStorage.setItem(ONBOARDING_DISMISSED_KEY, 'true');
  } catch {
    /* storage unavailable: hidden until reload */
  }
  listeners.forEach((listener) => listener());
}

/** For tests: forget the in-memory dismissal. */
export function resetOnboardingDismissedForTests(): void {
  dismissedInMemory = false;
  listeners.forEach((listener) => listener());
}

export function useOnboardingDismissed(): [boolean, () => void] {
  const dismissed = useSyncExternalStore(subscribe, readDismissed, readDismissed);
  const dismiss = useCallback(() => dismissOnboarding(), []);
  return [dismissed, dismiss];
}
